export const DEFAULT_PAGE_SIZE = 10;
export const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

export type PaginationMeta = {
  visibleCount: number;
  totalCount: number;
  page: number;
  totalPages: number;
  rangeStart: number;
  rangeEnd: number;
};

export type ApiPaginationFields = {
  showing_records?: string;
  page_info?: string;
  visible_records?: number;
  total_records?: number;
  current_page?: number;
  total_pages?: number;
  page_size?: number;
};

export type ApiPaginationResponse = {
  count?: number;
  next?: string | null;
  previous?: string | null;
  results?: unknown[];
  data?: unknown[];
  pagination_info?: ApiPaginationFields;
} & ApiPaginationFields;

export type ParsedListPagination = {
  results: unknown[];
  totalCount: number;
  currentPage: number;
  totalPages: number;
  pageSize: number;
  visibleCount: number;
  showingRecords: string;
  pageInfo: string;
  nextPage: string | null;
  prevPage: string | null;
};

type ParsePaginationOptions = {
  resultsKey?: "results" | "data" | "auto";
  fallbackPageSize?: number;
  fallbackPage?: number;
};

const resolveResults = (
  data: ApiPaginationResponse,
  resultsKey: ParsePaginationOptions["resultsKey"]
): unknown[] => {
  if (resultsKey === "data") {
    return Array.isArray(data.data) ? data.data : [];
  }
  if (resultsKey === "results") {
    return Array.isArray(data.results) ? data.results : [];
  }

  if (Array.isArray(data.results)) return data.results;
  if (Array.isArray(data.data)) return data.data;
  return [];
};

export const parsePaginationResponse = (
  data: ApiPaginationResponse | null | undefined,
  options: ParsePaginationOptions = {}
): ParsedListPagination => {
  const fallbackPageSize = options.fallbackPageSize ?? DEFAULT_PAGE_SIZE;
  const fallbackPage = options.fallbackPage ?? 1;
  const resultsKey = options.resultsKey ?? "auto";

  if (!data) {
    return {
      results: [],
      totalCount: 0,
      currentPage: fallbackPage,
      totalPages: 0,
      pageSize: fallbackPageSize,
      visibleCount: 0,
      showingRecords: "No records found",
      pageInfo: "",
      nextPage: null,
      prevPage: null,
    };
  }

  const results = resolveResults(data, resultsKey);
  const paginationFields = data.pagination_info ?? data;
  const totalCount =
    paginationFields.total_records ?? data.count ?? 0;
  const pageSize = paginationFields.page_size ?? fallbackPageSize;
  const currentPage = paginationFields.current_page ?? fallbackPage;
  const visibleCount = paginationFields.visible_records ?? results.length;
  const meta = getPaginationMeta(currentPage, totalCount, pageSize, visibleCount);
  const totalPages = paginationFields.total_pages ?? meta.totalPages;

  return {
    results,
    totalCount,
    currentPage,
    totalPages,
    pageSize,
    visibleCount,
    showingRecords:
      paginationFields.showing_records ??
      (totalCount > 0
        ? `Showing ${meta.rangeStart}-${meta.rangeEnd} of ${totalCount} records`
        : "No records found"),
    pageInfo:
      paginationFields.page_info ??
      (totalPages > 0 ? `Page ${meta.page} of ${totalPages}` : ""),
    nextPage: data.next ?? null,
    prevPage: data.previous ?? null,
  };
};

export const getPaginationMeta = (
  page: number,
  totalCount: number,
  pageSize: number,
  currentListLength: number
): PaginationMeta => {
  const size = pageSize > 0 ? pageSize : currentListLength;
  const totalPages =
    size > 0 && totalCount > 0
      ? Math.ceil(totalCount / size)
      : totalCount > 0
        ? 1
        : 0;

  const safePage = totalPages > 0 ? Math.min(page, totalPages) : 1;

  if (totalCount === 0 || currentListLength === 0) {
    return {
      visibleCount: 0,
      totalCount: 0,
      page: safePage,
      totalPages,
      rangeStart: 0,
      rangeEnd: 0,
    };
  }

  const rangeStart = (safePage - 1) * size + 1;
  const rangeEnd = Math.min(rangeStart + currentListLength - 1, totalCount);

  return {
    visibleCount: currentListLength,
    totalCount,
    page: safePage,
    totalPages,
    rangeStart,
    rangeEnd,
  };
};
